
import { useTranslation } from 'react-i18next';

import { useEffect } from 'react';
import UnsavedChangesPrompt from '../unsaved-changes-prompt/UnsavedChangesPrompt';
import { useConfirmation } from '../../contexts/confirmation-context/ConfirmationContext';
import { DefaultConfigurationFormValuesType, DefaultConfigurationType } from './Models';
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { useForm } from 'react-hook-form';
import ResponsiveDialog from '../responsive-dialog/ResponsiveDialog';
import Form from '../forms/form/Form';
import FormCheckbox from '../forms/form-checkbox/FormCheckbox';
import { Divider, Stack } from '@mui/material';
import FormRadioGroup, { FormRadioGroupRadioButton } from '../forms/form-radio-group/FormRadioGroup';

export type DefaultConfigurationDialogResult = DialogResult<DefaultConfigurationType>;
export type DefaultConfigurationDialogProps = DialogProps<DefaultConfigurationType, DefaultConfigurationDialogResult>;

export default function DefaultConfigurationDialog({
    open, 
    onClose,
    values,

}: DefaultConfigurationDialogProps) {
    
    const { t } = useTranslation();
    const { confirm
    //    , acknowledge 
    } = useConfirmation();

    const radioButtons: FormRadioGroupRadioButton[] = [{
        label: t('defaultConfiguration.usersWithoutDefault'),
        ariaLabel: 'users without default',
        value: 'withoutDefault'
    },{
        label: t('defaultConfiguration.allUsers', { groupName: values?.groupName ?? ''}),
        ariaLabel: 'all users',
        value: 'allUsers'
    }];
    

    const form = useForm<DefaultConfigurationFormValuesType>({
        mode: "onChange",
        defaultValues: {
            forCurrentUser: values?.forCurrentUser,
            //User Story 53044: Research - 2025 Q2 - Review GAS report
            forGroupOption: (
                values?.forUsersWithoutDefault 
                    ? 'withoutDefault' 
                    : values?.forAllUsers 
                        ? 'allUsers' 
                        : 'withoutDefault'
            ),
            forGroup: values?.forUsersWithoutDefault || values?.forAllUsers
        } as DefaultConfigurationFormValuesType
    });

    const {
        handleSubmit,
        formState: {
            isDirty,
            isValid,
        },
        reset,
        resetField,
        watch
    } = form;


    async function onSubmit(data: DefaultConfigurationFormValuesType) {

        const dialogValues: DefaultConfigurationType = {...values as DefaultConfigurationType,
            forAllUsers: data.forGroup && data.forGroupOption === 'allUsers',
            forUsersWithoutDefault: data.forGroup && data.forGroupOption === 'withoutDefault',
            forCurrentUser: data.forCurrentUser
        };

        reset();

        onClose && onClose({
            reason: "ok",
            values: dialogValues
        });
    }

    async function onCancel() {
        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            reset();
            onClose && onClose({
                reason: "cancel"
            });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText:  t("memoDialog.cancelEditText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    const watchAll = watch();

    useEffect(() => {

        reset({
            forCurrentUser: values?.forCurrentUser,
            forGroup: values?.forUsersWithoutDefault || values?.forAllUsers,
            //User Story 53044: Research - 2025 Q2 - Review GAS report
            forGroupOption: (
                values?.forUsersWithoutDefault 
                    ? 'withoutDefault' 
                    : values?.forAllUsers 
                        ? 'allUsers' 
                        : 'withoutDefault'
            )
        });
    }, [
        reset, 
        values
    ]);

    useEffect(() => {

        if (!watchAll.forGroup && watchAll.forGroupOption !== 'withoutDefault') {
            resetField('forGroupOption');
        }
        
    }, [
        watchAll.forGroup,
        watchAll.forGroupOption,
        resetField
    ]);

    return (
        <>
            <ResponsiveDialog
                title={ values?.title ?? t("defaultConfiguration.setAsDefaultView")}
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.apply")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onSubmit(data))()
                            : onCancel();
                    }
                }
            >
                <Form form={form}>

                    <Stack >
                        <FormCheckbox
                            name="forCurrentUser"
                            label={t("defaultConfiguration.myDefault")}
                        />

                        <Divider sx={{marginTop: 2}} />

                        <FormCheckbox sx={{marginTop: 2}}
                            name="forGroup"
                            label={t("defaultConfiguration.groupDefault")}
                            // rules={{
                            //     validate: (value: boolean) => {
                            //         return value && watchAll.forGroupOption !== '';
                                 
                            //     }
                            // }}

                        />
                        
                    <FormRadioGroup 
                        sx={{marginTop: 2, marginLeft: 2}}
                        disabled={!watchAll.forGroup}
                        label=''
                        name='forGroupOption'
                        radioButtons={radioButtons} 
                        rules={watchAll.forGroup ? {
                            required: true
                        // validate: (value: boolean) => {
                        //     return value && watchAll.forGroupOption !== '';
                            
                        // }
                        } : undefined}

                        />

                    </Stack>

                </Form>

            </ResponsiveDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </>
    );
}

export type DefaultConfigurationDialogState = DialogState<DefaultConfigurationType, DefaultConfigurationDialogResult>;
 export type BindDefaultConfigurationDialogReturn = BindDialogReturn<DefaultConfigurationType, DefaultConfigurationDialogResult>;

export function useDefaultConfigurationDialogState(): DefaultConfigurationDialogState {
    return useDialogState();
}

export function bindDefaultConfigurationDialog(props: DefaultConfigurationDialogState): BindDefaultConfigurationDialogReturn {
    return bindDialog(props);
}




